import {
  Box,
  Grid,
  Container,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import React from 'react';
import { motion } from 'framer-motion';
import BudgetCategories from './dashboard/BudgetCategories';
import DashboardHeader from './dashboard/DashboardHeader';
import MonthlySummary from './dashboard/MonthlySummary';
import RecentTransactions from './dashboard/RecentTransactions';
import PageTransition from './PageTransition';
import { DashboardTour } from './onboarding/DashboardTour';

const DashboardComponent = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <DashboardTour>
      <PageTransition>
        <Container 
          maxWidth={isMobile ? "sm" : "xl"} 
          sx={{ 
            py: { xs: 1, sm: 2 }, 
            px: { xs: 1, sm: 2, md: 3 },
            position: 'relative' 
          }}
          data-tour="dashboard"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-tour="month-navigation"
          >
            <DashboardHeader />
          </motion.div>
        
          {/* Use flex container to control order on mobile vs desktop */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 0
            }}
          >
            {/* On mobile: show grid first, summary last. On desktop: show summary first, grid last */}
            {isMobile ? (
              <>
                {/* Main Grid - Budget Categories and Recent Transactions (mobile: 2nd) */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                >
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    <Grid item xs={12} md={12} lg={8}>
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
                          backdropFilter: 'blur(10px)',
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          overflow: 'visible',
                          position: 'relative',
                          minHeight: 'fit-content',
                        }}
                        data-tour="budget-categories"
                      >
                        <BudgetCategories />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={12} lg={4}>
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
                          backdropFilter: 'blur(10px)',
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                        data-tour="recent-transactions"
                      >
                        <RecentTransactions />
                      </Box>
                    </Grid>
                  </Grid>
                  {/* Hidden target for drag and drop hint */}
                  <div data-tour="drag-drop-hint" style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    width: '300px', 
                    height: '200px', 
                    zIndex: 1,
                    pointerEvents: 'auto',
                    background: 'transparent'
                  }} />
                </motion.div>

                {/* Monthly Summary (mobile: last) */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  data-tour="monthly-summary"
                >
                  <MonthlySummary />
                </motion.div>
              </>
            ) : (
              <>
                {/* Monthly Summary (desktop: 2nd) */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  data-tour="monthly-summary"
                >
                  <MonthlySummary />
                </motion.div>
                
                {/* Main Grid - Budget Categories and Recent Transactions (desktop: last) */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                >
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    <Grid item xs={12} md={12} lg={8}>
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
                          backdropFilter: 'blur(10px)',
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          overflow: 'visible',
                          position: 'relative',
                          minHeight: 'fit-content',
                        }}
                        data-tour="budget-categories"
                      >
                        <BudgetCategories />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={12} lg={4}>
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
                          backdropFilter: 'blur(10px)',
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                        data-tour="recent-transactions"
                      >
                        <RecentTransactions />
                      </Box>
                    </Grid>
                  </Grid>
                  {/* Hidden target for drag and drop hint */}
                  <div data-tour="drag-drop-hint" style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    width: '300px', 
                    height: '200px', 
                    zIndex: 1,
                    pointerEvents: 'auto',
                    background: 'transparent'
                  }} />
                </motion.div>
              </>
            )}
          </Box>

          {/* Completion target */}
          <div data-tour="completion" style={{ 
            position: 'absolute', 
            top: '20%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            width: '400px', 
            height: '200px', 
            zIndex: -1,
            pointerEvents: 'none'
          }} />
        </Container>
      </PageTransition>
    </DashboardTour>
  );
});

// Export as a plain function for tests
const Dashboard = () => <DashboardComponent />;

export default Dashboard; 